import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, Field, Card, StatusPill, Select } from "@/components/kit";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";

const box = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] focus:outline-none focus:border-maroon/50";
const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export default function GardenHours() {
  const { me } = useAuth();
  const canEdit = me?.user?.is_super_admin || ["edit", "full"].includes(me?.permissions?.garden_hours);
  const [data, setData] = useState({ entries: [], by_member: [], grand_total_hours: 0 });
  const [plots, setPlots] = useState([]);
  const [q, setQ] = useState(""); const [matches, setMatches] = useState([]);
  const [f, setF] = useState({ member_id: "", member_name: "", plot_id: "", date: new Date().toISOString().slice(0, 10), hours: "", note: "" });
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => { const r = await api.get("/garden-hours"); setData(r.data); }, []);
  useEffect(() => { load(); api.get("/garden-plots", { params: { limit: 100 } }).then((r) => setPlots(r.data.plots)).catch(() => {}); }, [load]);

  useEffect(() => {
    if (q.trim().length < 1) { setMatches([]); return; }
    const t = setTimeout(() => api.get("/garden-plots/managers/search", { params: { q } }).then((r) => setMatches(r.data.matches)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (m) => { setF({ ...f, member_id: m.member_id, member_name: `${m.name} (${m.member_number})` }); setQ(""); setMatches([]); };
  const log = async () => {
    if (!f.member_id) return toast.error("Pick a gardener.");
    if (!parseFloat(f.hours)) return toast.error("Enter hours.");
    try {
      await api.post("/garden-hours", { member_id: f.member_id, plot_id: f.plot_id || null, date: f.date, hours: parseFloat(f.hours), note: f.note });
      toast.success("Hours logged"); setF({ ...f, member_id: "", member_name: "", hours: "", note: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log hours"); }
  };
  const del = (id) => setPending({ title: "Delete this hours entry?", fn: async () => { await api.delete(`/garden-hours/${id}`); load(); } });

  const thisMonth = data.entries.filter((e) => (e.date || "").slice(0, 7) === new Date().toISOString().slice(0, 7)).reduce((a, e) => a + (e.hours || 0), 0);

  return (
    <div data-testid="garden-hours-page">
      <ConfirmDelete open={!!pending} onClose={() => setPending(null)} onConfirm={() => pending?.fn()} title={pending?.title} message="This permanently removes the hours entry." />
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1">Garden Hours</h1>
      <p className="text-[16px] text-ink/70 mb-5">Volunteer time on the 100th Street plots, with running totals per gardener.</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <Card accent="#435912" className="!p-4" data-testid="hours-grand-total"><div className="text-[11px] font-bold uppercase tracking-wide text-ink/45 mb-1">Total hours logged</div><div className="font-zilla text-3xl font-semibold">{data.grand_total_hours}</div></Card>
        <Card accent="#daa405" className="!p-4"><div className="text-[11px] font-bold uppercase tracking-wide text-ink/45 mb-1">This month</div><div className="font-zilla text-3xl font-semibold">{Math.round(thisMonth * 100) / 100}</div></Card>
        <Card accent="#800000" className="!p-4"><div className="text-[11px] font-bold uppercase tracking-wide text-ink/45 mb-1">Active gardeners</div><div className="font-zilla text-3xl font-semibold">{data.by_member.length}</div></Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 space-y-4">
          {canEdit && (
            <Card data-testid="log-hours-card">
              <h3 className="font-zilla text-xl font-semibold mb-3">Log hours</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="relative sm:col-span-2">
                  <Field label="Gardener">
                    {f.member_id
                      ? <div className="flex items-center justify-between gap-2 bg-warm-cream rounded-lg px-3 py-2.5"><span className="text-[15px] font-semibold">{f.member_name}</span><button onClick={() => setF({ ...f, member_id: "", member_name: "" })}><Icon name="X" className="w-4 h-4" /></button></div>
                      : <input data-testid="hours-member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a gardener member…" className={box} />}
                  </Field>
                  {matches.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-cream border border-ink/15 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                      {matches.map((m) => <button key={m.member_id} data-testid={`hours-member-opt-${m.member_number}`} onClick={() => pick(m)} className="w-full text-left px-3 py-2.5 text-[15px] hover:bg-warm-cream border-b border-ink/8 last:border-0">{m.name} · {m.member_number}</button>)}
                    </div>
                  )}
                </div>
                <Field label="Plot (optional)"><Select data-testid="hours-plot" value={f.plot_id} onChange={(e) => setF({ ...f, plot_id: e.target.value })} className={box}><option value="">— No specific plot —</option>{plots.map((p) => <option key={p.plot_id} value={p.plot_id}>{p.location}</option>)}</Select></Field>
                <Field label="Date"><input type="date" data-testid="hours-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={box} /></Field>
                <Field label="Hours"><input type="number" step="0.25" min="0" data-testid="hours-amount" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} className={box} /></Field>
                <Field label="Note (optional)"><input data-testid="hours-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={box} /></Field>
              </div>
              <Button className="mt-3" data-testid="hours-log-btn" onClick={log}><Icon name="Plus" className="w-4 h-4" /> Log hours</Button>
            </Card>
          )}

          <Card className="!p-0 overflow-hidden" data-testid="hours-entries">
            <div className="px-5 py-3 border-b border-ink/10 font-zilla text-lg font-semibold">Recent entries</div>
            {data.entries.length === 0 && <div className="p-8 text-center text-ink/55">No hours logged yet.</div>}
            {data.entries.map((e) => (
              <div key={e.hours_id} data-testid={`hours-row-${e.hours_id}`} className="flex items-center gap-3 px-5 py-3 border-b border-ink/8 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold">{e.member_name} <span className="font-normal text-dark-olive">· {e.hours}h</span></div>
                  <div className="text-[12px] text-ink/55">{fmt(e.date)}{e.plot_label ? ` · ${e.plot_label}` : ""}{e.note ? ` · ${e.note}` : ""}</div>
                </div>
                {canEdit && <button data-testid={`hours-del-${e.hours_id}`} onClick={() => del(e.hours_id)} className="text-ink/40 hover:text-maroon"><Icon name="Trash2" className="w-4 h-4" /></button>}
              </div>
            ))}
          </Card>
        </div>

        <Card data-testid="hours-leaderboard">
          <h3 className="font-zilla text-xl font-semibold mb-3">Hours by gardener</h3>
          {data.by_member.length === 0 ? <p className="text-[13px] text-ink/50">No totals yet.</p>
            : data.by_member.map((m, i) => (
              <div key={m.member_id} className="flex items-center justify-between gap-2 py-2 border-b border-ink/8 last:border-0">
                <span className="text-[14px] flex items-center gap-2"><span className="text-ink/40 w-5">{i + 1}.</span>{m.name}</span>
                <StatusPill tone="olive">{m.hours}h</StatusPill>
              </div>
            ))}
        </Card>
      </div>
    </div>
  );
}
